import { CheckCircle2, FileImage, LockKeyhole, Sparkles } from 'lucide-react';
import { useLanguage } from '../i18n';

export function LandingSeoContent(): JSX.Element {
  const { t } = useLanguage();
  const steps = [
    ['01', t('landing.workflow1Title'), t('landing.workflow1Body')],
    ['02', t('landing.workflow2Title'), t('landing.workflow2Body')],
    ['03', t('landing.workflow3Title'), t('landing.workflow3Body')]
  ];
  const faqs = [1, 2, 3, 4].map((index) => ({
    question: t(`landing.faq${index}Q`),
    answer: t(`landing.faq${index}A`)
  }));

  return (
    <section className="mx-auto max-w-6xl px-4 pb-20 sm:px-6">
      <div className="grid gap-8 border-t border-[color:var(--line)] pt-12 lg:grid-cols-[1.1fr_0.9fr]">
        <div>
          <h2 className="max-w-2xl text-2xl font-semibold leading-tight sm:text-3xl">{t('landing.valueTitle')}</h2>
          <p className="mt-4 max-w-3xl text-sm leading-7 text-[color:var(--muted)]">{t('landing.valueBody')}</p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
          {[
            [LockKeyhole, t('landing.privacyTitle'), t('landing.privacyBody')],
            [Sparkles, t('landing.freeTitle'), t('landing.freeBody')],
            [FileImage, t('landing.formatsTitle'), t('landing.formatsBody')]
          ].map(([Icon, title, body]) => (
            <article key={String(title)} className="rounded-lg border border-[color:var(--line)] bg-[color:var(--panel)] p-4">
              <div className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[color:var(--panel-strong)] text-[color:var(--accent)]">
                  <Icon size={18} />
                </span>
                <div>
                  <h3 className="text-sm font-semibold">{title as string}</h3>
                  <p className="mt-2 text-sm leading-6 text-[color:var(--muted)]">{body as string}</p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>

      <div className="mt-12 grid gap-8 lg:grid-cols-[0.9fr_1.1fr]">
        <section>
          <h2 className="text-xl font-semibold">{t('landing.workflowTitle')}</h2>
          <div className="mt-4 grid gap-3">
            {steps.map(([number, title, body]) => (
              <article key={number} className="rounded-lg border border-[color:var(--line)] bg-[color:var(--panel)] p-4">
                <div className="flex gap-3">
                  <span className="text-xs font-semibold text-[color:var(--accent)]">{number}</span>
                  <div>
                    <h3 className="text-sm font-semibold">{title}</h3>
                    <p className="mt-1 text-sm leading-6 text-[color:var(--muted)]">{body}</p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold">{t('landing.faqTitle')}</h2>
          <div className="mt-4 divide-y divide-[color:var(--line)] rounded-lg border border-[color:var(--line)] bg-[color:var(--panel)]">
            {faqs.map((faq) => (
              <details key={faq.question} className="group p-4">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-sm font-semibold">
                  {faq.question}
                  <CheckCircle2 className="shrink-0 text-[color:var(--accent)]" size={16} />
                </summary>
                <p className="mt-3 text-sm leading-6 text-[color:var(--muted)]">{faq.answer}</p>
              </details>
            ))}
          </div>
        </section>
      </div>
    </section>
  );
}
